import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppRoot, LoginOkRoute } from '@/app/AppRoot';
import { APP_HOME_PATH } from '@/app/config/nav';
import { AppLayout } from '@/app/layouts/AppLayout';
import { ApprovalQueuePage } from '@/features/approvals/pages/ApprovalQueuePage';
import { ArticleCreatePage } from '@/features/articles/pages/ArticleCreatePage';
import { ArticlePreviewPage } from '@/features/articles/pages/ArticlePreviewPage';
import { ArticlesPage } from '@/features/articles/pages/ArticlesPage';
import { AutopilotLogPage } from '@/features/autopilotlog/pages/AutopilotLogPage';
import { CallTrackingPage } from '@/features/calltracking/pages/CallTrackingPage';
import { CampaignListPage } from '@/features/campaignlist/pages/CampaignListPage';
import { CampaignReportPage } from '@/features/campaignlist/pages/CampaignReportPage';
import { CreateCampaignPage } from '@/features/campaignlist/pages/CreateCampaignPage';
import { CreateCampaignPreviewPage } from '@/features/campaignlist/pages/CreateCampaignPreviewPage';
import { CampaignsPage } from '@/features/campaigns/pages/CampaignsPage';
import { CitationsPage } from '@/features/citations/pages/CitationsPage';
import { AllCompetitorsPage } from '@/features/competitors/pages/AllCompetitorsPage';
import { CompetitorCrawlReportPage } from '@/features/competitors/pages/CompetitorCrawlReportPage';
import { CompetitorDossierPage } from '@/features/competitors/pages/CompetitorDossierPage';
import { WebsiteHealthPage } from '@/features/website-health/pages/WebsiteHealthPage';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { GbpManagerPage } from '@/features/gbp2/pages/GbpManagerPage';
import { GbpPhotosBulkUploadPage } from '@/features/gbp2/pages/GbpPhotosBulkUploadPage';
import { GbpPhotosPage } from '@/features/gbp2/pages/GbpPhotosPage';
import { GbpPage } from '@/features/gbp/pages/GbpPage';
import { KeywordRankingsPage } from '@/features/keywords/pages/KeywordRankingsPage';
import { LandingPageCreatePage } from '@/features/landingpages/pages/LandingPageCreatePage';
import { LandingPagePreviewPage } from '@/features/landingpages/pages/LandingPagePreviewPage';
import { LandingPagesPage } from '@/features/landingpages/pages/LandingPagesPage';
import { LeadsPage } from '@/features/leads/pages/LeadsPage';
import { LocalMarketIntelligencePage } from '@/features/local-market/pages/LocalMarketIntelligencePage';
import { MarketPage } from '@/features/market/pages/MarketPage';
import { MonthlyReportPage } from '@/features/monthlyreport/pages/MonthlyReportPage';
import { SchemaPage } from '@/features/schema/pages/SchemaPage';
import { GrowthSettingsPage } from '@/features/settings2/pages/GrowthSettingsPage';
import { SettingsPage } from '@/features/settings/pages/SettingsPage';
import { SettingsOauthCallbackPage } from '@/features/settings/pages/SettingsOauthCallbackPage';
import { SETTINGS_DEFAULT_SECTION } from '@/features/settings/routes';
import { NotFoundPage } from '@/features/shared/pages/NotFoundPage';
import { RouteErrorPage } from '@/features/shared/pages/RouteErrorPage';

export const router = createBrowserRouter([
  {
    element: <AppRoot />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: '/loginok',
        element: <LoginOkRoute />,
      },
      {
        path: '/',
        element: <AppLayout />,
        children: [
          { index: true, element: <Navigate to={APP_HOME_PATH} replace /> },
          { path: 'dashboard', element: <DashboardPage /> },

          // Local (first impression)
          { path: 'local-market-intelligence', element: <LocalMarketIntelligencePage /> },
          { path: 'market', element: <MarketPage /> },
          { path: 'competitors', element: <AllCompetitorsPage /> },
          { path: 'competitors/:competitorId', element: <CompetitorDossierPage /> },
          {
            path: 'competitors/:competitorId/crawl',
            element: <CompetitorCrawlReportPage />,
          },

          // Campaigns (Google Ads)
          { path: 'campaigns', element: <CampaignListPage /> },
          { path: 'campaigns/create', element: <CreateCampaignPage /> },
          {
            path: 'campaigns/create/preview/:suggestionId',
            element: <CreateCampaignPreviewPage />,
          },
          { path: 'campaigns/:campaignId', element: <CampaignReportPage /> },
          { path: 'suggested-campaigns', element: <CampaignsPage /> },

          { path: 'leads', element: <LeadsPage /> },

          // SEO & tracking
          { path: 'keywords', element: <KeywordRankingsPage /> },
          { path: 'call-tracking', element: <CallTrackingPage /> },

          // Google Business Profile
          { path: 'gbp', element: <GbpManagerPage /> },
          { path: 'gbp/photos', element: <GbpPhotosPage /> },
          { path: 'gbp/photos/bulk-upload', element: <GbpPhotosBulkUploadPage /> },
          { path: 'map-pack', element: <GbpPage /> },

          { path: 'citations', element: <CitationsPage /> },

          // Content & landing pages
          { path: 'content', element: <ArticlesPage /> },
          { path: 'articles/new', element: <ArticleCreatePage /> },
          { path: 'articles/:articleId', element: <ArticlePreviewPage /> },
          { path: 'landing-pages', element: <LandingPagesPage /> },
          { path: 'landing-pages/new', element: <LandingPageCreatePage /> },
          { path: 'landing-pages/preview', element: <LandingPagePreviewPage /> },
          { path: 'landing-pages/:landingPageId', element: <LandingPagePreviewPage /> },
          { path: 'website-health', element: <WebsiteHealthPage /> },

          { path: 'schema', element: <SchemaPage /> },

          // Automation & reports
          { path: 'approval-queue', element: <ApprovalQueuePage /> },
          { path: 'autopilot-log', element: <AutopilotLogPage /> },
          { path: 'monthly-report', element: <MonthlyReportPage /> },

          // Settings
          { path: 'settings/oauth/callback', element: <SettingsOauthCallbackPage /> },
          {
            path: 'settings',
            element: <Navigate to={`/settings/${SETTINGS_DEFAULT_SECTION}`} replace />,
          },
          { path: 'settings/:section', element: <GrowthSettingsPage /> },
          { path: 'setup-checklist', element: <SettingsPage /> },

          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
